import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Bell, Smartphone, Battery, Shield, FileText, CheckCircle2, AlertTriangle, X } from 'lucide-react';
import { awaabsLawInfo, legalRequirements, dampDefenderKits, dampDetectionFeatures, complianceBenefits, testimonial } from '../data/awaabsLawData';
import { companyInfo } from '../data/mockData';

const AwaabsLaw = () => {
  const [selectedKit, setSelectedKit] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);

  const handleOrderClick = (kit) => {
    setSelectedKit(kit);
    setShowOrderModal(true);
  };

  const closeModal = () => {
    setShowOrderModal(false);
    setSelectedKit(null);
  };

  const getIcon = (iconName) => {
    const icons = {
      Clock: Clock,
      Bell: Bell,
      Smartphone: Smartphone,
      Battery: Battery,
      Shield: Shield,
      FileText: FileText
    };
    const IconComponent = icons[iconName] || Shield;
    return <IconComponent size={32} />;
  };

  return (
    <div className="page">
      {/* Hero Section */}
      <section className="page-hero">
        <div className="container">
          <div className="page-hero-content">
            <div className="badge">UK Regulation Compliance</div>
            <h1 className="heading-1">{awaabsLawInfo.title}</h1>
            <p className="body-large">
              {awaabsLawInfo.subtitle}
            </p>
            <Link to="/contact" className="btn-primary">
              Get Compliance Solution
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* What is Awaab's Law */}
      <section className="section">
        <div className="container">
          <div className="law-intro">
            <div className="law-intro-content">
              <h2 className="heading-2">What is Awaab's Law?</h2>
              <p className="body-large">
                {awaabsLawInfo.description}
              </p>
              <div className="law-alert">
                <AlertTriangle size={24} />
                <div>
                  <strong>Effective: {awaabsLawInfo.effectiveDate}</strong>
                  <p className="body-small">Part of the Social Housing (Regulation) Act 2023. Applies to social housing landlords in England.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Legal Requirements */}
      <section className="section bg-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2">Legal Requirements</h2>
            <p className="section-subtitle">
              Strict timeframes landlords must follow.
            </p>
          </div>

          <div className="requirements-grid">
            {legalRequirements.map((req, index) => (
              <div key={index} className="requirement-card">
                <div className="requirement-timeframe">{req.timeframe}</div>
                <h3 className="heading-3">{req.title}</h3>
                <p className="body-medium">{req.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solution: Damp Detection System */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2">Full-Bin UK Damp Detection System</h2>
            <p className="section-subtitle">
              Sensative sensors + Yggio platform = Complete compliance solution
            </p>
          </div>

          <div className="solution-intro" style={{maxWidth: '800px', margin: '0 auto', textAlign: 'center'}}>
            <p className="body-large">
              Our damp detection system uses Sensative's award-winning LoRaWAN sensors to provide 24/7 monitoring of humidity, temperature, and water leaks. Catch problems early, respond within legal timeframes, and protect your tenants.
            </p>
            <div className="solution-features" style={{maxWidth: '600px', margin: '2rem auto', textAlign: 'left'}}>
              <div className="solution-feature-item">
                <CheckCircle2 size={20} />
                <span>Ultra-thin Strips sensors (10-year battery)</span>
              </div>
              <div className="solution-feature-item">
                <CheckCircle2 size={20} />
                <span>Drip leak detection sensors</span>
              </div>
              <div className="solution-feature-item">
                <CheckCircle2 size={20} />
                <span>LoRaWAN Gateway for connectivity</span>
              </div>
              <div className="solution-feature-item">
                <CheckCircle2 size={20} />
                <span>Yggio cloud platform included</span>
              </div>
            </div>
          </div>

          <div className="ai-grid" style={{marginTop: '3rem'}}>
            {dampDetectionFeatures.map((feature, index) => (
              <div key={index} className="product-card">
                <div className="card-icon">
                  {getIcon(feature.icon)}
                </div>
                <h3 className="product-card-title">{feature.title}</h3>
                <p className="product-card-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Detection Kits */}
      <section className="section bg-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2">Choose Your Detection Kit</h2>
            <p className="section-subtitle">
              Ready-made solutions with Sensative sensors and 10-year battery life.
            </p>
          </div>

          <div className="kits-grid">
            {dampDefenderKits.map((kit, index) => (
              <div key={index} className={`kit-card ${kit.popular ? 'kit-popular' : ''}`}>
                {kit.popular && <div className="popular-badge">Most Popular</div>}
                <h3 className="heading-3">{kit.name}</h3>
                <p className="kit-ideal">{kit.ideal}</p>
                <div className="kit-price">
                  <div className="price-main">{kit.price}</div>
                  <div className="price-weekly">As low as {kit.weeklyPrice}/week</div>
                </div>
                <div className="kit-includes">
                  <div className="includes-label">Includes:</div>
                  <ul className="includes-list">
                    {kit.includes.map((item, i) => (
                      <li key={i}>
                        <CheckCircle2 size={16} />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <button 
                  onClick={() => handleOrderClick(kit)} 
                  className="btn-primary"
                  style={{width: '100%', border: 'none', cursor: 'pointer'}}
                >
                  Order Now
                  <ArrowRight size={20} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance Benefits */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2">Why Early Detection Matters</h2>
          </div>

          <div className="benefits-list" style={{maxWidth: '900px', margin: '0 auto'}}>
            {complianceBenefits.map((benefit, index) => (
              <div key={index} className="benefit-item">
                <CheckCircle2 size={24} />
                <p className="body-medium">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cost Savings */}
      <section className="section bg-section">
        <div className="container">
          <div className="cost-comparison">
            <h2 className="heading-2">Prevent £2,000-£3,000 Repairs</h2>
            <p className="body-large">
              One mould incident can cost thousands in repairs, legal fees, and disruption. Our damp detection system typically pays for itself many times over by catching problems early.
            </p>
            <div className="cost-stats">
              <div className="cost-stat">
                <div className="stat-value">£2,500</div>
                <div className="stat-label">Average mould remediation cost</div>
              </div>
              <div className="cost-stat">
                <div className="stat-value">£379</div>
                <div className="stat-label">Starter detection kit</div>
              </div>
              <div className="cost-stat">
                <div className="stat-value">87%</div>
                <div className="stat-label">Potential cost savings</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="section">
        <div className="container">
          <div className="testimonial-large">
            <p className="testimonial-quote-large">"{testimonial.quote}"</p>
            <div className="testimonial-author-large">
              <div className="author-name">{testimonial.author}</div>
              <div className="author-role">{testimonial.role}</div>
              <div className="author-company">{testimonial.properties}</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="heading-2">Ready to comply with Awaab's Law?</h2>
            <p className="body-large">
              Contact us to discuss your damp detection needs and ensure compliance.
            </p>
            <Link to="/contact" className="btn-primary">
              Get Your Detection Kit
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Order Modal */}
      {showOrderModal && selectedKit && (
        <div className="order-modal-overlay" onClick={closeModal}>
          <div className="order-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>
              <X size={24} />
            </button>
            
            <h2 className="heading-2">Order {selectedKit.name}</h2>
            <p className="body-medium">Complete your order for Sensative damp detection kit</p>

            <div className="order-summary">
              <div className="order-kit-info">
                <h3 className="heading-3">{selectedKit.name}</h3>
                <p className="body-small">{selectedKit.ideal}</p>
                <div className="order-price">
                  <span className="price-large">{selectedKit.price}</span>
                  <span className="price-note">or {selectedKit.weeklyPrice}/week</span>
                </div>
              </div>

              <div className="order-includes">
                <h4 className="body-medium" style={{fontWeight: 600, marginBottom: '1rem'}}>Kit Includes:</h4>
                <ul className="includes-list">
                  {selectedKit.includes.map((item, i) => (
                    <li key={i}>
                      <CheckCircle2 size={16} />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="order-contact">
              <h3 className="heading-3">Choose Payment Method:</h3>
              
              <div className="payment-methods">
                {/* Stripe Checkout Button - Only show if payment link exists */}
                {selectedKit.stripePaymentLink ? (
                  <>
                    <a 
                      href={selectedKit.stripePaymentLink} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="btn-primary stripe-btn"
                      style={{textDecoration: 'none'}}
                    >
                      <span>💳</span>
                      <div>
                        <strong>Pay with Card</strong>
                        <span className="body-small">Secure Stripe Checkout</span>
                      </div>
                    </a>

                    <div className="payment-divider">
                      <span>or</span>
                    </div>

                    <h4 className="body-medium" style={{fontWeight: 600, marginTop: '1rem'}}>Contact for Alternative Payment:</h4>
                  </>
                ) : (
                  <h4 className="body-medium" style={{fontWeight: 600}}>Contact to Order:</h4>
                )}
                
                <div className="contact-methods">
                  <a href={`mailto:${companyInfo.email}?subject=Order: ${selectedKit.name}&body=I would like to order the ${selectedKit.name} (${selectedKit.price}). Please provide bank transfer or purchase order details.`} className="contact-method-btn">
                    <span>📧</span>
                    <div>
                      <strong>Email Order</strong>
                      <span className="body-small">{companyInfo.email}</span>
                    </div>
                  </a>

                  <a href={`tel:${companyInfo.phone}`} className="contact-method-btn">
                    <span>📞</span>
                    <div>
                      <strong>Call to Order</strong>
                      <span className="body-small">{companyInfo.phone}</span>
                    </div>
                  </a>
                </div>
              </div>

              <div className="payment-info">
                <h4 className="body-medium" style={{fontWeight: 600}}>All Payment Options:</h4>
                <ul className="body-small" style={{listStyle: 'disc', paddingLeft: '1.5rem', marginTop: '0.5rem'}}>
                  <li>Credit/Debit Card (instant via Stripe)</li>
                  <li>Bank transfer</li>
                  <li>Purchase order</li>
                  <li>Weekly payment plan available ({selectedKit.weeklyPrice}/week)</li>
                </ul>
              </div>
            </div>

            <button onClick={closeModal} className="btn-secondary" style={{width: '100%', marginTop: '1rem'}}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AwaabsLaw;
